import dayjs from "dayjs";
import {
  BorderStyle,
  HeadingLevel,
  PageBreak,
  Paragraph,
  TextRun,
  UnderlineType,
} from "docx";
import {
  getGroupData,
  getGroupName,
  getPoaIndex,
  getSelectedPeriod,
} from "../../../../services/GroupContext";
import { processDateJS } from "../../../../utils/dateProcessor";
import {
  defaultFontNormal,
  headerTitleStyle,
  indentationNormal,
} from "./styling";

export function getWordReportingCompanies(savedData, poaIndex) {
  //Get the Active Reporting Company Data including the Appointment Date, which is not saved in the PoA Data. Maybe export this as a function somewhere for ease?
  if (savedData?.reporting_company && savedData?.reporting_company.length > 0) {
    const reportingCompanyList = savedData?.reporting_company?.map((Rcomp) => {
      // Drafts may not exist in savedData.companies, so we handle them separately
      const company =
        savedData?.companies?.find(
          (c) => c?.company_ID === Rcomp?.company_ID,
        ) || Rcomp;

      if (company) {
        return {
          company_name: company?.company_name,
          tax_office: company?.tax_office,
          utr: company?.utr,
          company_ID: company?.company_ID,
          first_appointed_period_end_date:
            Rcomp?.first_appointed_period_end_date,
          appointment_date: Rcomp?.appointment_date,
          appointer: Rcomp?.appointer,
          nomination_link: Rcomp?.nomination_link,
          reason_appointment_lapsed: Rcomp?.reason_appointment_lapsed,
          revocation_date: Rcomp?.revocation_date,
          first_period_appointment_lapsed:
            Rcomp?.first_period_appointment_lapsed,
          status: Rcomp?.status, // Keep status for filtering
        };
      }
      return null;
    });

    const active = reportingCompanyList?.filter((comp) => {
      if (comp?.status !== "Active") return false;
      const today = dayjs();
      const currentPoAEnd = processDateJS(
        savedData.periods_of_account[poaIndex]?.period_end,
      )?.utc();
      const poaEndDate = processDateJS(
        comp?.first_appointed_period_end_date,
      )?.utc();
      const firstPeriodLapsed = processDateJS(
        comp?.first_period_appointment_lapsed,
      )?.utc();
      const revocationDate = processDateJS(comp?.revocation_date)?.utc();

      if (comp.appointer === "HMRC" && poaEndDate?.isSame(currentPoAEnd)) {
        return true;
      }

      if (
        comp.appointer === "Company" &&
        poaEndDate?.isSameOrBefore(currentPoAEnd) &&
        !comp.first_period_appointment_lapsed
      ) {
        return true;
      }

      if (
        comp.appointer === "Company" &&
        poaEndDate?.isSameOrBefore(currentPoAEnd) &&
        firstPeriodLapsed?.isAfter(currentPoAEnd)
      ) {
        return true;
      }

      if (
        comp.appointer === "Company" &&
        poaEndDate?.isSameOrBefore(currentPoAEnd) &&
        firstPeriodLapsed?.isSame(currentPoAEnd) &&
        comp.reason_appointment_lapsed === "Manual Revocation" &&
        revocationDate?.isAfter(today)
      ) {
        return true;
      }

      return false;
    });
    return active;
  } else return null;
}

export function sortByCompanyNameAndStartDate(companies) {
  return companies.sort((comp1, comp2) => {
    const nameCompare = comp1.company_name.localeCompare(comp2.company_name);
    if (nameCompare !== 0) return nameCompare;
    return new Date(comp1.start_date) - new Date(comp2.start_date);
  });
}

// Return a list (or array) of companies and CPD_total amounts for each company in the period, in both local currency (stating the currency) and GBP
export function getCompaniesCPDTotals() {
  const selectPoA = getSelectedPeriod();

  const companiesCPD = selectPoA?.adjusted_caps
    ?.filter((c) => c?.cpd_total_gbp)
    .map((c) => ({
      company_ID: c.company_ID,
      company_name: c.company_name,
      currency: c.currency,
      cpd_total: c.cpd_total,
      cpd_total_gbp: c.cpd_total_gbp,
      start_date: c.start_date,
      end_date: c.end_date,
    }));

  return sortByCompanyNameAndStartDate(companiesCPD);
}

// Return a list (or array) of companies and CPR_total amounts for each company in the period, in both local currency (stating the currency) and GBP
export function getCompaniesCPRTotals() {
  const selectPoA = getSelectedPeriod();

  const companiesCPR = selectPoA?.adjusted_caps
    ?.filter((c) => c?.cpr_total_gbp)
    .map((c) => ({
      company_ID: c.company_ID,
      company_name: c.company_name,
      currency: c.currency,
      cpr_total: c.cpr_total,
      cpr_total_gbp: c.cpr_total_gbp,
      start_date: c.start_date,
      end_date: c.end_date,
    }));

  return sortByCompanyNameAndStartDate(companiesCPR);
}

// Return a list of companies with s407_relief_rd_expenditure_credit > 0, with the array to have the company and the amount in local currency for any corresponding accounting period that overlaps with the period of account
export function getCompaniesExpenditureCredit() {
  const selectPoA = getSelectedPeriod();
  // const relevant_APs = get_relevant_periods(selectPoA.period_start, selectPoA.period_end, group_data);

  const companies = selectPoA.adjusted_caps
    .filter((ap) => ap?.s407_relief_rd_expenditure_credit?.value > 0)
    .map((company) => ({
      company_ID: company.company_ID,
      company_name: company.company_name,
      s407_relief_rd_expenditure_credit:
        company.s407_relief_rd_expenditure_credit.value,
    }));

  return companies;
}

// Return an array with a list of companies with any corresponding accounting period that overlaps with the period of account that has non-null data for one of the bottom three lines below
// Company Name -
// Relevant Accounting Period - "Start Date - End Date"
// Credit for foreign tax claimed under s18(2) TIOPA 2010 - net_dtr_adjustment_lc
// Tax-EBITDA adjustment - s407_relief_dtr_adjustment_tax_rate_equal_uk
// Tax-Interest income adjustment - nltr_exclude_tax_interest_income_double_relief + ntdc_exclude_tax_interest_income_double_relief + tlr_exclude_tax_interest_income_double_relief + tdc_exclude_tax_interest_income_double_relief + ofa_exclude_tax_interest_income_double_relief

export function getCompaniesTax() {
  const nonNullKeys = [
    "net_dtr_adjustment_lc",
    "s407_relief_dtr_adjustment_tax_rate_equal_uk",
    "nltr_exclude_tax_interest_income_double_relief",
    "ntdc_exclude_tax_interest_income_double_relief",
    "tlr_exclude_tax_interest_income_double_relief",
    "tdc_exclude_tax_interest_income_double_relief",
    "ofa_exclude_tax_interest_income_double_relief",
  ];

  const selectPoA = getSelectedPeriod();
  // const relevant_APs = get_relevant_periods(selectPoA.period_start, selectPoA.period_end, group_data);

  const companies = selectPoA.adjusted_caps
    .filter((cap) => nonNullKeys.some((key) => cap[key] !== null))
    .map((company) => ({
      company_ID: company.company_ID,
      company_name: company.company_name,
      relevant_ap: `${company.start_date} - ${company.end_date}`,
      currency: company.currency,
      net_dtr_adjustment_lc: company.net_dtr_adjustment_lc?.value ?? 0,
      s407_relief_dtr_adjustment_tax_rate_equal_uk:
        company.s407_relief_dtr_adjustment_tax_rate_equal_uk?.value ?? 0,
      tax_interest_income_adjustment:
        (company.nltr_exclude_tax_interest_income_double_relief?.value ?? 0) +
        (company.ntdc_exclude_tax_interest_income_double_relief?.value ?? 0) +
        (company.tlr_exclude_tax_interest_income_double_relief?.value ?? 0) +
        (company.tdc_exclude_tax_interest_income_double_relief?.value ?? 0) +
        (company.ofa_exclude_tax_interest_income_double_relief?.value ?? 0),
    }))
    .reduce((acc, company) => {
      if (!acc[company.company_name]) {
        acc[company.company_name] = {
          company_ID: company.company_ID,
          company_name: company.company_name,
          net_dtr_adjustment_lc: 0,
          s407_relief_dtr_adjustment_tax_rate_equal_uk: 0,
          tax_interest_income_adjustment: 0,
          currency: company.currency,
        };
      }

      acc[company.company_name].net_dtr_adjustment_lc +=
        company.net_dtr_adjustment_lc;
      acc[company.company_name].s407_relief_dtr_adjustment_tax_rate_equal_uk +=
        company.s407_relief_dtr_adjustment_tax_rate_equal_uk;
      acc[company.company_name].tax_interest_income_adjustment +=
        company.tax_interest_income_adjustment;

      return acc;
    }, {});
  return Object.values(companies);
}

// Return an array with a list of companies with any corresponding accounting period that overlaps with the period of account that has non-null data for one of the bottom line below
// Company Name -
// Relevant Accounting Period - "Start Date - End Date"
// s407_relief_finance_lease_depreciation + s407_relief_reversal_of_lease_depreciation

export function getCompaniesDepreciation() {
  const nonNullKeys = [
    // "start_date",
    // "end_date",
    "s407_relief_finance_lease_depreciation",
    "s407_relief_reversal_of_lease_depreciation",
  ];

  const selectPoA = getSelectedPeriod();
  // const group_data = getGroupData();
  // const relevant_APs = get_relevant_periods(selectPoA.period_start, selectPoA.period_end, group_data);

  const companies = selectPoA.adjusted_caps
    .filter((cap) => nonNullKeys.some((key) => cap[key] !== null))
    .map((company) => ({
      company_ID: company.company_ID,
      company_name: company.company_name,
      currency: company.currency,
      // relevant_ap_days: processDateJS(company.end_date).diff(processDateJS(company.start_date)),
      relevant_ap: `${company.start_date} - ${company.end_date}`,
      relief_lease:
        (company.s407_relief_finance_lease_depreciation?.value ?? 0) -
        (company.s407_relief_reversal_of_lease_depreciation?.value ?? 0),
    }));
  return companies;
}

export function getCompaniesAdjustments() {
  const nonNullKeys = [
    "s407_relief_long_funding_lease_deduction",
    "s407_relief_long_funding_lessee",
    "s407_relief_finance_lease_rentals_receivable",
    "s407_relief_finance_lease_depreciation",
  ];

  const selectedPoA = getSelectedPeriod();
  const companies = selectedPoA.adjusted_caps
    .filter((cap) => nonNullKeys.some((key) => cap[key] !== null))
    .map((company) => ({
      company_ID: company.company_ID,
      company_name: company.company_name,
      currency: company.currency,
      deduction: company.s407_relief_long_funding_lease_deduction?.value ?? 0,
      reduction: company.s407_relief_long_funding_lessee?.value ?? 0,
      capitalRentalComponent:
        company.s407_relief_finance_lease_rentals_receivable?.value ?? 0,
      // depreciation: company.s407_relief_finance_lease_depreciation?.value ?? 0
      depreciation: "",
    }));

  return companies;
}

export const getBase64FromUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getTodayDate = () => {
  const date = new Date();

  const options = { month: "short", day: "numeric", year: "numeric" };
  const formattedDate = date.toLocaleDateString("en-US", options);

  return formattedDate;
};

export const getDocumentData = (isClientFacing = true) => {
  const savedData = getGroupData();
  const poaIndex = getPoaIndex();
  const poaData = savedData?.periods_of_account[poaIndex];

  const groupName = getGroupName();

  const poaStart = processDateJS(poaData?.period_start).format("DD/MM/YYYY");
  const formattedPoAStart = processDateJS(poaStart)?.format("DD MMMM YYYY");
  const poaEnd = processDateJS(poaData?.period_end).format("DD/MM/YYYY");
  const formattedPoAEnd = processDateJS(poaEnd)?.format("DD MMMM YYYY");

  const ultimateParent = savedData.companies.find(
    (comp) => comp.company_ID === savedData.ult_parent_ID,
  );
  const ultimateParentName = ultimateParent?.company_name;

  const groupDisallowance = poaData.group_disallowance ?? 0;

  const amendedFilingDeadline = processDateJS(poaEnd)
    ?.add(3, "year")
    .format("DD/MM/YYYY");

  const formattedAmendedFilingDeadline = processDateJS(
    amendedFilingDeadline,
  )?.format("DD MMMM YYYY");

  const filingDeadline = processDateJS(poaEnd)
    ?.add(1, "year")
    .format("DD MMMM YYYY");

  const ReportingCompanyData = getWordReportingCompanies(savedData, poaIndex);
  const reportingCompanyName = ReportingCompanyData?.[0]?.company_name
    ? ReportingCompanyData[0]?.company_name
    : "";
  const reportingCompanyAppointedDate = ReportingCompanyData?.[0]
    ?.appointment_date
    ? processDateJS(ReportingCompanyData[0]?.appointment_date).format(
        "DD MMMM YYYY",
      )
    : "";
  const reportingCompanyAppointer = ReportingCompanyData?.[0]?.appointer
    ? ReportingCompanyData[0]?.appointer
    : "";

  const nonConsentingCompaniesExist = poaData?.adjusted_caps?.some(
    (cap) => cap.non_consenting_company,
  );

  const groupHasClaim = poaData?.adjusted_caps?.some(
    (cap) =>
      (cap?.s407_relief_rd_additional_relief?.value ?? 0) !== 0 ||
      (cap?.s407_relief_rd_expenditure_credit?.value ?? 0) !== 0,
  );

  const companiesCPDTotals = getCompaniesCPDTotals();
  const companiesCPRTotals = getCompaniesCPRTotals();

  const cpdTotalSum = companiesCPDTotals.reduce(
    (sum, company) => sum + company.cpd_total,
    0,
  );
  const cprTotalSum = companiesCPRTotals.reduce(
    (sum, company) => sum + company.cpr_total,
    0,
  );
  const totalUnusedAllowance = savedData.unused_allowances_for_group.reduce(
    (sum, allowance) => sum + allowance.unused_generated,
    0,
  );

  return {
    savedData,
    poaData,
    poaIndex,
    poaStart,
    formattedPoAStart,
    poaEnd,
    formattedPoAEnd,
    groupName,
    ultimateParentName,
    groupDisallowance,
    amendedFilingDeadline,
    formattedAmendedFilingDeadline,
    filingDeadline,
    reportingCompanyName,
    reportingCompanyAppointedDate,
    reportingCompanyAppointer,
    nonConsentingCompaniesExist,
    groupHasClaim,
    companiesCPDTotals,
    companiesCPRTotals,
    cpdTotalSum,
    cprTotalSum,
    totalUnusedAllowance,
    isClientFacing,
  };
};

export const getExtraFooter = (superScriptNum, text) => {
  const superscript = new TextRun({
    text: superScriptNum,
    superScript: true,
    ...defaultFontNormal,
  });

  const mainText = new TextRun({ text, ...defaultFontNormal });

  const extraFooter = new Paragraph({
    children: [superscript, mainText],
    border: {
      top: {
        color: "auto",
        space: 1,
        style: BorderStyle.SINGLE,
        size: 6,
      },
    },
    ...indentationNormal,
    //   indent: {
    //     right: 3000,
    //   },
  });

  return extraFooter;
};

export const getSectionHeader = (title, includeInTOC = true, breaks) => {
  return new Paragraph({
    ...(includeInTOC ? { heading: HeadingLevel.HEADING_1 } : {}),
    children: [
      new PageBreak(),
      new TextRun({
        text: title,
        ...headerTitleStyle,
      }),
      ...(breaks ? [new TextRun({ break: breaks })] : []),
    ],
  });
};

// scope and exculsions: bullet dot, text, font, color, break
// summary: condition, bullet dot, text, font, color, break
// identification: bullet dot, text, font, color, break
// poa: bullet dot, text, font, color, break
// reporting company: condition, text, font, superscript, color, break
// consenting/non consenting companies: condition, text, font, color, break
// filing deadline: text, font, color, break
// outcome of calculations
// disallowance: condition, dot, text, font, color, break, underline
// no disallowance: condition, dot, text, font, color, break, underline
// nature of return: condition, text, font, color, break, underline
// elections: text, font, break
// rdec: condition, dot, text, font, color, break
// capitalised rde: condition, text, font, superscript, color, break
// dtr: condition, text, font, color, break
// derivatives: condition, dot, text, font, color, break
// bank charges: text, font, color, break
// appendix 1: condition, dot, text, font, break
// appendix 2: bullet number, text, font, break, underline
const getBulletConfig = (paragraph) => {
  if (paragraph.bullet === "number") {
    return {
      numbering: {
        reference: `numbering-${paragraph.bulletNum}`,
        level: 0,
      },
    };
  } else if (paragraph.bullet === "dot" && paragraph.bullet_level) {
    return {
      bullet: {
        level: paragraph.bullet_level,
      },
    };
  } else if (paragraph.bullet === "dot") {
    return {
      bullet: {
        level: 0,
      },
    };
  } else {
    return {};
  }
};

export const generateSectionParagraphs = (paragraphs) => {
  return paragraphs
    .filter(
      (paragraph) => paragraph.condition || paragraph.condition === undefined,
    )
    .map(
      (paragraph) =>
        new Paragraph({
          ...getBulletConfig(paragraph),
          ...(paragraph.indentation ?? {}),
          ...(paragraph.spaceBefore
            ? { spacing: { before: paragraph.spaceBefore } }
            : {}),
          children: paragraph.segments.map(
            (segment) =>
              new TextRun({
                text: segment.text,
                ...segment.font,
                ...(segment.size ?? {}),
                ...(segment.color ? { color: segment.color } : {}),
                ...(segment.break ? { break: segment.break } : {}),
                ...(segment.superScript ? { superScript: true } : {}),
                ...(segment.underline
                  ? { underline: { type: UnderlineType.SINGLE } }
                  : {}),
              }),
          ),
        }),
    );
};

export const formatNumber = (num) => {
  if (!num) return "0";

  const positiveFormat = Math.abs(Math.round(num ?? 0)).toLocaleString();
  const negativeFormat = `(${positiveFormat})`;

  return num >= 0 ? positiveFormat : negativeFormat;
};
